import React, { useState, useEffect } from 'react';
import { X, CreditCard, Key } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';

interface PaymentModalProps {
  isOpen: boolean;
  onClose: () => void;
  onPayment: () => void;
  onActivationSuccess?: () => void;
}

const PaymentModal: React.FC<PaymentModalProps> = ({ isOpen, onClose, onPayment, onActivationSuccess }) => {
  const [loading, setLoading] = useState(false);
  const [showActivationCode, setShowActivationCode] = useState(false);
  const [activationCode, setActivationCode] = useState('');
  const [cardNumber, setCardNumber] = useState('');
  const [expiryDate, setExpiryDate] = useState('');
  const [cvv, setCvv] = useState('');
  const [savedScrollPosition, setSavedScrollPosition] = useState(0);
  const { activateWithCode } = useAuth();

  // Save and restore scroll position when modal opens/closes
  useEffect(() => {
    if (isOpen) {
      // Save current scroll position
      setSavedScrollPosition(window.scrollY);
    } else {
      // Restore scroll position when modal closes
      if (savedScrollPosition > 0) {
        window.scrollTo(0, savedScrollPosition);
      }
    }
  }, [isOpen, savedScrollPosition]);

  if (!isOpen) return null;

  // Format card number with spaces
  const formatCardNumber = (value: string) => {
    return value
      .replace(/\s/g, '')
      .replace(/(.{4})/g, '$1 ')
      .trim()
      .slice(0, 19); // Max 16 digits + 3 spaces
  };

  // Format expiry date MM/YY
  const formatExpiryDate = (value: string) => {
    return value
      .replace(/\D/g, '')
      .replace(/(\d{2})(\d)/, '$1/$2')
      .slice(0, 5);
  };

  const handlePayment = async () => {
    // Basic validation
    if (!cardNumber.trim() || !expiryDate.trim() || !cvv.trim()) {
      alert('Please fill in all payment fields');
      return;
    }

    setLoading(true);
    // Simulate payment processing
    await new Promise(resolve => setTimeout(resolve, 2000));
    setLoading(false);
    onPayment();
  };

  const handleActivationCode = async () => {
    if (!activationCode.trim()) return;
    
    setLoading(true);
    try {
      const result = await activateWithCode(activationCode);
      if (result.success) {
        // Use onActivationSuccess if provided, otherwise onPayment
        if (onActivationSuccess) {
          onActivationSuccess();
        } else {
          onPayment();
        }
      } else {
        alert(result.error || 'Invalid activation code');
      }
    } catch (error) {
      alert('Failed to activate. Please try again.');
    }
    setLoading(false);
  };

  return (
    <div 
      className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4"
      onClick={onClose}
    >
      <div 
        className="bg-white max-w-md w-full p-6 relative shadow-2xl max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={onClose}
          className="absolute right-4 top-4 text-gray-400 hover:text-gray-600 transition-colors"
        >
          <X className="h-5 w-5" />
        </button>

        <div className="text-center">
          <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-orange-100 mb-4">
            <CreditCard className="h-6 w-6 text-orange-600" />
          </div>

          <h3 className="text-lg font-bold text-gray-900 mb-6">
            Unlock Best Value Running Shoes
          </h3>

          <div className="space-y-3 text-left mb-6">
            <div>
              <span className="text-sm text-gray-700">💰 <span className="text-orange-600 font-bold">Save $50-$100 guaranteed</span> or your money back</span>
            </div>
            <div>
              <span className="text-sm text-gray-700">🎫 Get premium discount links right away</span>
            </div>
          </div>

          <div className="bg-orange-50 rounded-lg p-4 mb-6">
            <div className="text-center">
              <div className="text-xl font-bold text-gray-900">$4.99/month</div>
              <div className="text-xs text-gray-500 mt-1">30-day access to premium discount links</div>
            </div>
          </div>

          {!showActivationCode ? (
            <div className="space-y-3">
              <div className="space-y-3">
                <input
                  type="text"
                  placeholder="Card number"
                  value={cardNumber}
                  onChange={(e) => setCardNumber(formatCardNumber(e.target.value))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                />
                
                <div className="grid grid-cols-2 gap-3">
                  <input
                    type="text"
                    placeholder="MM/YY"
                    value={expiryDate}
                    onChange={(e) => setExpiryDate(formatExpiryDate(e.target.value))}
                    className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                  />
                  <input
                    type="text"
                    placeholder="CVV"
                    value={cvv}
                    onChange={(e) => setCvv(e.target.value.replace(/\D/g, '').slice(0, 4))}
                    className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                  />
                </div>
              </div>
              
              <button
                onClick={handlePayment}
                disabled={loading || !cardNumber.trim() || !expiryDate.trim() || !cvv.trim()}
                className="w-full bg-orange-600 hover:bg-orange-700 text-white font-semibold py-3 px-4 rounded-lg transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? (
                  <div className="flex items-center justify-center gap-2">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    Processing Payment...
                  </div>
                ) : (
                  'Pay $4.99 - Save $50-$100'
                )}
              </button>
              
              <button
                onClick={onClose}
                className="w-full bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium py-2 px-4 rounded-lg transition-all duration-300"
              >
                Cancel
              </button>
            </div>
          ) : (
            <div className="space-y-3">
              <div className="mb-4 space-y-3">
                <input
                  type="text"
                  placeholder="Enter activation code"
                  value={activationCode}
                  onChange={(e) => setActivationCode(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                />
              </div>
              
              <button
                onClick={handleActivationCode}
                disabled={loading || !activationCode.trim()}
                className="w-full bg-green-600 hover:bg-green-700 text-white font-semibold py-3 px-4 rounded-lg transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? (
                  <div className="flex items-center justify-center gap-2">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    Activating...
                  </div>
                ) : (
                  <div className="flex items-center justify-center gap-2">
                    <Key className="h-4 w-4" />
                    Activate Code
                  </div>
                )}
              </button>
              
              <button
                onClick={() => setShowActivationCode(false)}
                className="w-full bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium py-2 px-4 rounded-lg transition-all duration-300"
              >
                Back to Payment
              </button>
            </div>
          )}

          <div className="mt-6 text-center">
            {!showActivationCode && (
              <button
                onClick={() => setShowActivationCode(true)}
                className="text-xs text-gray-400 hover:text-gray-600 underline transition-colors mb-3"
              >
                Have an activation code? Click here
              </button>
            )}
            
            <div className="text-xs text-gray-500">
              30-day access to exclusive deals. Non-refundable except as guaranteed above.
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PaymentModal;